import React, { useEffect, useState } from 'react';
import moment, { Duration, Moment } from 'moment';
import 'moment-duration-format';
import './PayrollView.css';

// Get base path for API calls (app is at domain root)
const getBasePath = () => {
  return '/databaseOps/';
};

// Get authentication headers with JWT token
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

type Worker = {
  ID: number;
  Name: string;
  Admin: boolean;
  EMail: string;
  hourly_rate?: number;
  contracted_hours_per_month?: number;
  mobile_phone?: string;
  employment_start_date?: string;
  employment_end_date?: string;
};

type Worktime = {
  ID: number;
  MitarbeiterID: number;
  Datum: number;
  Beginn: number;
  Ende: number;
  Pause: number;
  Bemerkung: string;
  Arbeitszeit: number;
};

async function loadAllWorkers(): Promise<Worker[]> {
  const response = await fetch(`${getBasePath()}getListOfWorkers.php`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch workers: ${response.statusText}`);
  }

  return response.json();
}

async function loadWorkersWorktime(id: number): Promise<Worktime[]> {
  const response = await fetch(`${getBasePath()}getAllWorktimeOfWorkerID.php`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: `id=${id}`,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch worktime: ${response.statusText}`);
  }

  return response.json();
}

function PayrollView({ onNavigate }: { onNavigate?: (view: string) => void }) {
  return (
    <div className="payroll-container">
      <div className="payroll-header">
        <h2>Arbeitnehmer, Zeiten und Löhne</h2>
        {onNavigate && (
          <button className="btn-manage-employees" onClick={() => onNavigate('employees')}>
            Mitarbeiter verwalten
          </button>
        )}
      </div>
      <ListOfWorkers />
    </div>
  );
}

function ListOfWorkers() {
  const [angestellte, setAngestellte] = useState<Worker[]>([]);
  const [activeDate, setActiveDate] = useState(moment());

  useEffect(() => {
    loadAllWorkers().then((r) => {
      setAngestellte(r);
    });
  }, []);

  const lastMonth = () => {
    setActiveDate(activeDate.clone().subtract(1, 'month'));
  };

  const nextMonth = () => {
    setActiveDate(activeDate.clone().add(1, 'month'));
  };

  return (
    <div className="payroll-content">
      <div className="month-navigation">
        <button className="nav-arrow" onClick={lastMonth}>
          ◀
        </button>
        <div className="month-display">
          Zeitraum vom{' '}
          {activeDate.clone().startOf('month').format('DD.MM.YYYY')} -{' '}
          {activeDate.clone().endOf('month').format('DD.MM.YYYY')}
        </div>
        <button className="nav-arrow" onClick={nextMonth}>
          ▶
        </button>
      </div>
      <div className="workers-list">
        {angestellte.map((a) => (
          <WorkerData key={a.ID} w={a} date={activeDate} />
        ))}
      </div>
    </div>
  );
}

function WorkerData(props: { w: Worker; date: Moment }) {
  const [worktime, setWorktime] = useState<Worktime[]>([]);

  useEffect(() => {
    loadWorkersWorktime(props.w.ID).then((wt) => {
      const filteredWt = wt.filter((w) => {
        const date = moment(w.Datum * 1);
        return date.isBetween(
          props.date.clone().startOf('month'),
          props.date.clone().endOf('month'),
          'month',
          '[]'
        );
      });
      setWorktime(filteredWt);
    });
  }, [props.w.ID, props.date]);

  let groupedByBemerkung = worktime.reduce(
    (
      acc: {
        [key: string]: {
          Bemerkung: string;
          rate: number;
          totalArbeitszeit: moment.Duration;
          totalSalary: number;
        };
      },
      wt: Worktime
    ) => {
      let previousDuration =
        acc[wt.Bemerkung]?.totalArbeitszeit || moment.duration();
      let currentDuration = moment.duration(wt.Arbeitszeit, 'milliseconds');

      // Use hourly rate from database, or fallback to legacy hardcoded rates
      let rate: number;
      if (props.w.hourly_rate && props.w.hourly_rate > 0) {
        rate = props.w.hourly_rate;
      } else {
        // Legacy fallback for employees without hourly_rate set
        if (
          wt.Bemerkung === 'Unterricht' ||
          wt.Bemerkung === 'Reitstunden' ||
          wt.Bemerkung === 'Reitunterricht' ||
          wt.Bemerkung === 'Reiten'
        ) {
          rate = 15.0;
        } else {
          rate = props.w.ID === 38 ? 13.5 : 13.0;
        }
      }

      let previousSalary = acc[wt.Bemerkung]?.totalSalary || 0;
      let currentSalary = currentDuration.asHours() * rate;

      acc[wt.Bemerkung] = {
        Bemerkung: wt.Bemerkung,
        rate: rate,
        totalArbeitszeit: previousDuration.add(currentDuration),
        totalSalary: previousSalary + currentSalary,
      };

      return acc;
    },
    {}
  );

  let totalSalary = Object.values(groupedByBemerkung).reduce(
    (total, { totalSalary }) => total + totalSalary,
    0
  );
  let totalArbeitszeit = Object.values(groupedByBemerkung).reduce(
    (total, { totalArbeitszeit }) => total.add(totalArbeitszeit),
    moment.duration()
  );

  if (
    totalArbeitszeit.asMilliseconds() === moment.duration(0).asMilliseconds()
  ) {
    return null;
  }

  return (
    <div className="worker-card">
      <h3 className="worker-name">{props.w.Name}</h3>
      <div className="work-entries">
        {Object.entries(groupedByBemerkung).map(
          ([bemerkungKey, { Bemerkung, rate, totalArbeitszeit, totalSalary }]) => (
            <div className="work-entry" key={bemerkungKey}>
              <WorkTime Bemerkung={Bemerkung} Arbeitszeit={totalArbeitszeit} />
              <Lohn Salary={totalSalary} Rate={rate} />
            </div>
          )
        )}
      </div>
      <div className="total-line">
        <div className="total-hours">
          {Math.floor(totalArbeitszeit.asHours())}:
          {totalArbeitszeit.minutes().toString().padStart(2, '0')} Stunden
        </div>
        <div className="total-net">
          {(totalSalary / 1.19).toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
          })}{' '}
          netto
        </div>
        <div className="total-gross">
          {totalSalary.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
          })}
        </div>
      </div>
    </div>
  );
}

function WorkTime(props: { Bemerkung: string; Arbeitszeit: Duration }) {
  return (
    <div className="work-time">
      <div className="work-description">{props.Bemerkung}</div>
      <div className="work-duration">
        {Math.floor(props.Arbeitszeit.asHours())}:
        {props.Arbeitszeit.minutes().toString().padStart(2, '0')}
      </div>
    </div>
  );
}

function Lohn(props: { Salary: number; Rate: number }) {
  return (
    <div className="salary">
      <div className="rate">
        {props.Rate.toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
        })}
      </div>
      <div className="total">
        {props.Salary.toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
        })}
      </div>
    </div>
  );
}

export default PayrollView;
